/**
 * ArchivePage -- superadmin only.
 *
 * Архивная машина не показывается в списке машин, на сайте («Kde vytisknete»)
 * и в офлайн-алертах; heartbeat и история при этом сохраняются. Сюда попали
 * легаси-номера первого сетевого модуля (416/418/419/420); когда такой номер
 * оживает на новом автомате, его возвращают отсюда одной кнопкой.
 */

import { useEffect, useState } from 'react';
import { getArchivedMachines, unarchiveMachine, type ArchivedMachine } from '../api/client';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString();
}

export function ArchivePage() {
  const [rows, setRows] = useState<ArchivedMachine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows(await getArchivedMachines());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load archive');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleRestore(m: ArchivedMachine) {
    const ok = confirm(
      `Restore machine ${m.name} (ID ${m.machineId}) from the archive?\n\n` +
      `It will be back in the machine list, on the public site and in offline alerts.`,
    );
    if (!ok) return;
    setBusyId(m.machineId);
    try {
      await unarchiveMachine(m.machineId);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Restore failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2>Archive</h2>
      </div>

      <div className="card">
        <div className="card-header">Archived machines ({rows.length})</div>
        <div className="card-body">
          <p className="text-muted" style={{ marginTop: 0 }}>
            Archived machines are hidden from the machine list, the public site and offline alerts.
            Their data and heartbeats are kept. To archive a machine, open its page and press “Archive”.
          </p>
          {loading && <div className="empty-state"><p className="text-muted">Loading...</p></div>}
          {error && <div className="login-error">{error}</div>}
          {!loading && !error && rows.length === 0 && (
            <div className="empty-state"><p className="text-muted">The archive is empty.</p></div>
          )}
          {!loading && rows.length > 0 && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Last seen</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={m.machineId}>
                      <td>{m.machineId}</td>
                      <td>{m.name}</td>
                      <td>{m.location ?? '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(m.lastSeenAt)}</td>
                      <td>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={busyId === m.machineId}
                          onClick={() => handleRestore(m)}
                        >
                          {busyId === m.machineId ? 'Restoring...' : 'Restore from archive'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
